import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  TouchableOpacity,
  Modal,
  ScrollView,
  Animated,
  Dimensions,
  Alert,
  Platform,
  useWindowDimensions,
} from 'react-native';
import SvgIcon from '../components/SvgIcon';
import colors from '../theme/colors';
import * as api from '../services/api';

const BRIDE_ICON = 'https://kalyanamitra.in/assets/ps-bride-UWGtbgl1.png';
const GROOM_ICON = 'https://kalyanamitra.in/assets/ps-groom-D-L2Y5H0.png';

const PhotosTabScreen = ({ shortProfile, onPhotoChange, isOwnProfile, isMediatorProfile }) => {
  const { height: windowHeight } = useWindowDimensions();
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [fullscreenPhotoVisible, setFullscreenPhotoVisible] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [photosState, setPhotosState] = useState(shortProfile?.photos || []);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [uploadingTileIndex, setUploadingTileIndex] = useState(null);
  const [dialog, setDialog] = useState({ visible: false, type: '', title: '', message: '', onConfirm: null });
  const [toast, setToast] = useState({ visible: false, message: '' });

  // Update photos state when shortProfile changes
  React.useEffect(() => {
    if (shortProfile?.photos) {
      console.log('Photos updated, count:', shortProfile.photos.length);
      setPhotosState(shortProfile.photos);
      // Reset active index if needed
      if (activePhotoIndex >= shortProfile.photos.length && shortProfile.photos.length > 0) {
        setActivePhotoIndex(0);
      }
    }
  }, [shortProfile?.photos]);
  
  // Photo zoom and pan states
  const scale = useRef(new Animated.Value(1)).current;
  const translateX = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(0)).current;
  const lastScale = useRef(1);
  const lastTranslateX = useRef(0);
  const lastTranslateY = useRef(0);
  const [currentZoomPercent, setCurrentZoomPercent] = useState(100);
  const panStartRef = useRef({ x: 0, y: 0 });
  const isPanningRef = useRef(false);

  const memberId = shortProfile?.memberId;
  const defaultIcon = shortProfile?.gender === 'Male' ? GROOM_ICON : BRIDE_ICON;
  
  // Calculate responsive photo height to minimize white space
  // Reserve space for thumbnails (100px) and bottom nav (80px)
  // Use 70% of available space for photo on larger screens, min 300px
  const availableHeight = windowHeight - 180; // Reserve for thumbnails + bottom nav
  const photoHeight = Math.max(300, Math.min(availableHeight, windowHeight * 0.7));

  const showDialog = (type, title, message, onConfirm = null) => {
    setDialog({ visible: true, type, title, message, onConfirm });
  };

  const hideDialog = () => {
    setDialog({ visible: false, type: '', title: '', message: '', onConfirm: null });
  };

  const handleDialogConfirm = () => {
    if (dialog.onConfirm) {
      dialog.onConfirm();
    }
    hideDialog();
  };

  const showToast = (message) => {
    setToast({ visible: true, message });
    setTimeout(() => {
      setToast({ visible: false, message: '' });
    }, 5000);
  };

  // Refresh photos by reloading parent profile data
  const refreshPhotos = async () => {
    try {
      console.log('Refreshing photos for member:', memberId);
      if (onPhotoChange) {
        await onPhotoChange();
        console.log('Profile data refreshed successfully');
      }
    } catch (err) {
      console.error('Error refreshing photos:', err);
    }
  };

  const resetZoom = () => {
    Animated.parallel([
      Animated.spring(scale, { toValue: 1, useNativeDriver: true }),
      Animated.spring(translateX, { toValue: 0, useNativeDriver: true }),
      Animated.spring(translateY, { toValue: 0, useNativeDriver: true })
    ]).start();
    lastScale.current = 1;
    lastTranslateX.current = 0;
    lastTranslateY.current = 0;
    setCurrentZoomPercent(100);
  };

  const navigatePhoto = (direction) => {
    const photos = photosState;
    if (direction === 'next' && activePhotoIndex < photos.length - 1) {
      setActivePhotoIndex(activePhotoIndex + 1);
    } else if (direction === 'prev' && activePhotoIndex > 0) {
      setActivePhotoIndex(activePhotoIndex - 1);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    setIsLoading(true);
    setError(null);
    try {
      console.log('=== DELETE PHOTO START ===');
      console.log('Member ID:', memberId);
      console.log('Photo ID:', photoId);
      
      const photoToDelete = photosState.find(p => p.photoId === photoId);
      console.log('Photo object:', photoToDelete);
      console.log('Photo object keys:', Object.keys(photoToDelete || {}));
      
      // Extract the FULLY DECODED key from securedUrl
      let photoKey = null;
      if (photoToDelete?.securedUrl) {
        const url = new URL(photoToDelete.securedUrl);
        // pathname gives us a decoded path, but it might still be partially encoded
        // Decode it fully to get the raw key
        const pathname = url.pathname.substring(1); // Remove leading slash
        photoKey = decodeURIComponent(pathname); // Fully decode
        console.log('Extracted FULLY DECODED key:', photoKey);
      }
      
      console.log('Final photo key to use:', photoKey);
      
      if (!photoKey) {
        console.error('Photo object structure:', JSON.stringify(photoToDelete, null, 2));
        throw new Error('Photo key not found. Cannot delete photo.');
      }
      
      const response = await api.deleteProfilePhoto({ 
        memberId, 
        photoId,
        photoKey: photoKey
      });
      console.log('Delete response:', response);
      console.log('Delete response status:', response.status);
      console.log('Delete response data:', response.data);
      
      // Check if delete was actually successful
      if (response.status !== 200 && response.status !== 204) {
        throw new Error('Delete operation may have failed. Status: ' + response.status);
      }
      
      console.log('=== DELETE PHOTO SUCCESS ===');
      
      // Refresh photos from server to update parent components
      await refreshPhotos();
      
      showDialog('success', 'Success', 'Photo deleted successfully!');
    } catch (err) {
      console.error('=== DELETE PHOTO ERROR ===');
      console.error('Error object:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      setError(err.message || 'Failed to delete photo');
      
      // Check if it's a network error
      if (!navigator.onLine || err.message?.toLowerCase().includes('network') || err.code === 'ERR_NETWORK') {
        showToast('No internet connection. Please check your network and try again.');
      } else {
        showDialog('error', 'Delete Failed', err.message || 'Unknown error. Check console for details.');
      }
    }
    setIsLoading(false);
  };

  const confirmDelete = (photoId) => {
    showDialog(
      'confirm',
      'Delete Photo',
      'Are you sure you want to delete this photo?',
      () => handleDeletePhoto(photoId)
    );
  };

  const handleDoneEditing = async () => {
    // Refresh profile data to get latest photos from server BEFORE exiting edit mode
    if (onPhotoChange) {
      await refreshPhotos();
    }
    setEditMode(false);
  };

  const handleUploadPhoto = async (fileUriOrFile, tileIndex = 0) => {
    if (photosState.length >= 4) {
      showDialog('error', 'Upload Limit', 'Maximum 4 photos allowed');
      return;
    }
    
    // First photo (index 0) is always the profile photo
    const isProfilePhoto = tileIndex === 0;
    
    setIsLoading(true);
    setError(null);
    setUploadProgress(0);
    setUploadingTileIndex(tileIndex); // Track which tile is being uploaded
    
    // Store file reference and create preview URL for immediate display
    let localPreviewUrl = null;
    
    try {
      // Get file details
      let file, fileName, contentType, fileSizeBytes;
      
      if (Platform.OS === 'web') {
        file = fileUriOrFile;
        fileName = `${Date.now()}_${file.name}`;
        contentType = file.type;
        fileSizeBytes = file.size;
        // Create local blob URL for immediate preview
        localPreviewUrl = URL.createObjectURL(file);
      } else {
        // For native platforms - read file from URI
        const { FileSystem } = require('expo-file-system');
        const fileExtension = fileUriOrFile.split('.').pop().toLowerCase();
        fileName = `${Date.now()}_photo.${fileExtension}`;
        contentType = `image/${fileExtension}`;
        
        console.log('Reading file from native URI:', fileUriOrFile);
        
        // Read file as base64
        const base64Data = await FileSystem.readAsStringAsync(fileUriOrFile, {
          encoding: FileSystem.EncodingType.Base64,
        });
        
        // Get file info for size
        const fileInfo = await FileSystem.getInfoAsync(fileUriOrFile);
        fileSizeBytes = fileInfo.size || 0;
        
        // Convert base64 to binary string, then to Uint8Array
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // On React Native, we can pass the Uint8Array directly to uploadToS3
        // Or wrap it in an object with the data
        file = {
          data: bytes,
          isNative: true
        };
        
        // Use the native URI for preview
        localPreviewUrl = fileUriOrFile;
        
        console.log('File read successfully:', { fileName, contentType, fileSizeBytes });
      }
      
      console.log('Step 1: Getting presigned URL...', { memberId, fileName, contentType });
      
      // Step 1: Get presigned URL
      const uploadUrlResponse = await api.getUploadUrl({
        memberId,
        fileName,
        contentType
      });
      
      console.log('Upload URL response:', uploadUrlResponse.data);
      const { uploadUrl, photoKey } = uploadUrlResponse.data?.data || uploadUrlResponse.data;
      
      if (!uploadUrl || !photoKey) {
        throw new Error('Invalid response from upload-url endpoint');
      }
      
      console.log('Step 2: Uploading to S3...', { uploadUrl, photoKey });
      
      // Step 2: Upload to S3 with progress tracking
      await api.uploadToS3(uploadUrl, file, contentType, (progress) => {
        console.log(`Upload progress: ${progress.toFixed(0)}%`);
        setUploadProgress(progress);
      });
      
      console.log('Step 3: Saving photo metadata...');
      
      // Step 3: Save metadata to backend
      const saveResponse = await api.savePhotoMetadata({
        memberId,
        photoKey,
        s3Url: uploadUrl.split('?')[0], // Remove query params from URL
        fileName,
        contentType,
        fileSizeBytes,
        isProfilePhoto,
        photoIndex: tileIndex // Use the tile index as photoIndex
      });
      
      console.log('Save metadata response:', saveResponse.data);
      const newPhoto = saveResponse.data?.data || saveResponse.data;
      
      console.log('Photo saved successfully:', newPhoto);
      
      // Refresh photos from server to get the correct S3 URL and display in grid
      console.log('Starting refresh, current editMode:', editMode);
      setUploadProgress(null);
      setUploadingTileIndex(null);
      await refreshPhotos();
      
      // Use setTimeout to ensure state has updated before re-enabling edit mode
      console.log('Refresh complete, re-enabling edit mode');
      setTimeout(() => {
        setEditMode(true);
        console.log('Edit mode re-enabled');
      }, 100);
      
      showDialog('success', 'Success', 'Photo uploaded successfully!');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Failed to upload photo');
      setUploadProgress(null);
      setUploadingTileIndex(null);
      
      // Check if it's a network error
      if (!navigator.onLine || err.message?.toLowerCase().includes('network') || err.code === 'ERR_NETWORK') {
        showToast('No internet connection. Please check your network and try again.');
      } else {
        showDialog('error', 'Upload Failed', err.message || 'Unknown error');
      }
    }
    
    setIsLoading(false);
  };

  // File picker for upload (Expo ImagePicker with ImageCropPicker)
  const pickImage = async (tileIndex = 0) => {
    if (Platform.OS === 'web') {
      // Web: Use native file input
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          console.log('File selected:', file.name, file.size);
          // Convert file to base64 or blob URL for web
          const reader = new FileReader();
          reader.onload = async () => {
            try {
              await handleUploadPhoto(file, tileIndex);
            } catch (err) {
              console.error('Upload error:', err);
            }
          };
          reader.readAsDataURL(file);
        }
      };
      input.click();
    } else {
      // Native: Use ImageCropPicker with proper crop UI
      try {
        const ImagePicker = require('expo-image-picker');
        const ImageCropPicker = require('react-native-image-crop-picker');
        
        // Step 1: Request permissions
        const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
        if (status !== 'granted') {
          showDialog('error', 'Permission Required', 'Please grant camera roll permissions to upload photos.');
          return;
        }
        
        // Step 2: Use expo-image-picker without crop to get the image
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: ImagePicker.MediaTypeOptions.Images,
          allowsEditing: false, // Don't use expo's built-in crop
          quality: 0.8,
        });
        
        if (!result.canceled && result.assets?.length > 0) {
          const selectedImageUri = result.assets[0].uri;
          console.log('Image selected:', selectedImageUri);
          
          // Step 3: Use ImageCropPicker for proper crop UI with visible buttons
          try {
            const croppedImage = await ImageCropPicker.openCropper({
              path: selectedImageUri,
              width: 300,
              height: 400,
              cropping: true,
              cropperActiveWidgetColor: '#14B8A6',
              cropperToolbarColor: '#0F172A',
              cropperStatusBarColor: '#0F172A',
              cropperTitleTextColor: '#ffffff',
              cropperToolbarWidgetColor: '#ffffff',
              includeBase64: true,
              compressImageQuality: 0.8,
            });
            
            console.log('Image cropped:', croppedImage.path || croppedImage);
            // Use the cropped image URI
            await handleUploadPhoto(croppedImage.path, tileIndex);
          } catch (cropError) {
            console.error('Crop error:', cropError);
            if (cropError.message?.includes('User cancelled')) {
              // User cancelled the crop, that's fine
              return;
            }
            throw cropError;
          }
        }
      } catch (err) {
        console.error('Image picker error:', err);
        setError('Failed to pick image: ' + err.message);
        Alert.alert('Error', 'Failed to pick image: ' + err.message);
      }
    }
  };

  const renderFullscreenPhotoModal = () => {
    if (!shortProfile) return null;
    
    const photos = shortProfile.photos || [];
    const currentPhoto = photos[activePhotoIndex];
    const defaultIcon = shortProfile.gender === 'Male' ? GROOM_ICON : BRIDE_ICON;

    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      const newScale = Math.max(1, Math.min(lastScale.current + delta, 3));
      lastScale.current = newScale;
      scale.setValue(newScale);
      setCurrentZoomPercent(Math.round(newScale * 100));
    };

    const handleMouseDown = (e) => {
      if (lastScale.current > 1) {
        isPanningRef.current = true;
        panStartRef.current = { x: e.nativeEvent.pageX, y: e.nativeEvent.pageY };
      }
    };

    const handleMouseMove = (e) => {
      if (isPanningRef.current && lastScale.current > 1) {
        const dx = e.nativeEvent.pageX - panStartRef.current.x;
        const dy = e.nativeEvent.pageY - panStartRef.current.y;
        lastTranslateX.current += dx;
        lastTranslateY.current += dy;
        translateX.setValue(lastTranslateX.current);
        translateY.setValue(lastTranslateY.current);
        panStartRef.current = { x: e.nativeEvent.pageX, y: e.nativeEvent.pageY };
      }
    };

    const handleMouseUp = () => {
      isPanningRef.current = false;
    };

    const handleTouchStart = (e) => {
      if (lastScale.current > 1 && e.nativeEvent.touches.length === 1) {
        isPanningRef.current = true;
        const touch = e.nativeEvent.touches[0];
        panStartRef.current = { x: touch.pageX, y: touch.pageY };
      }
    };

    const handleTouchMove = (e) => {
      if (isPanningRef.current && lastScale.current > 1 && e.nativeEvent.touches.length === 1) {
        const touch = e.nativeEvent.touches[0];
        const dx = touch.pageX - panStartRef.current.x;
        const dy = touch.pageY - panStartRef.current.y;
        lastTranslateX.current += dx;
        lastTranslateY.current += dy;
        translateX.setValue(lastTranslateX.current);
        translateY.setValue(lastTranslateY.current);
        panStartRef.current = { x: touch.pageX, y: touch.pageY };
      }
    };

    const handleTouchEnd = () => {
      isPanningRef.current = false;
    };

    return (
      <Modal
        visible={fullscreenPhotoVisible}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setFullscreenPhotoVisible(false)}
      >
        <View style={styles.fullscreenModal}>
          <TouchableOpacity 
            style={styles.closeButton}
            onPress={() => {
              resetZoom();
              setFullscreenPhotoVisible(false);
            }}
          >
            <SvgIcon name="close" size={32} color={colors.white} />
          </TouchableOpacity>

          <View 
            style={styles.fullscreenPhotoContainer}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <Animated.Image
              source={{ uri: currentPhoto?.securedUrl || defaultIcon }}
              style={[
                styles.fullscreenPhoto,
                {
                  transform: [
                    { scale: scale },
                    { translateX: translateX },
                    { translateY: translateY }
                  ],
                  cursor: lastScale.current > 1 ? 'grab' : 'default'
                }
              ]}
              resizeMode="contain"
            />
          </View>

          {activePhotoIndex > 0 && (
            <TouchableOpacity 
              style={[styles.navButton, styles.navButtonLeft]}
              onPress={() => {
                resetZoom();
                navigatePhoto('prev');
              }}
            >
              <SvgIcon name="chevron-back" size={40} color={colors.white} />
            </TouchableOpacity>
          )}
          
          {activePhotoIndex < photos.length - 1 && (
            <TouchableOpacity 
              style={[styles.navButton, styles.navButtonRight]}
              onPress={() => {
                resetZoom();
                navigatePhoto('next');
              }}
            >
              <SvgIcon name="chevron-forward" size={40} color={colors.white} />
            </TouchableOpacity>
          )}

          <View style={styles.photoCounter}>
            <Text style={styles.photoCounterText}>
              {activePhotoIndex + 1} / {photos.length}
            </Text>
          </View>
          
          <View style={styles.zoomControls}>
            <TouchableOpacity 
              style={styles.zoomButton}
              onPress={() => {
                const newScale = Math.max(1, lastScale.current - 0.5);
                lastScale.current = newScale;
                scale.setValue(newScale);
                setCurrentZoomPercent(Math.round(newScale * 100));
                if (newScale === 1) {
                  translateX.setValue(0);
                  translateY.setValue(0);
                  lastTranslateX.current = 0;
                  lastTranslateY.current = 0;
                }
              }}
            >
              <SvgIcon name="remove" size={24} color={colors.white} />
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.zoomButton}
              onPress={() => resetZoom()}
            >
              <Text style={styles.zoomButtonText}>{currentZoomPercent}%</Text>
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.zoomButton}
              onPress={() => {
                const newScale = Math.min(3, lastScale.current + 0.5);
                lastScale.current = newScale;
                scale.setValue(newScale);
                setCurrentZoomPercent(Math.round(newScale * 100));
              }}
            >
              <SvgIcon name="add" size={24} color={colors.white} />
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    );
  };

  if (!shortProfile) return null;
  
  // For view mode, sort photos by photoIndex
  const photos = [...photosState].sort((a, b) => {
    const indexA = a.photoIndex !== undefined ? a.photoIndex : 999;
    const indexB = b.photoIndex !== undefined ? b.photoIndex : 999;
    return indexA - indexB;
  });
  
  const currentPhoto = photos[activePhotoIndex];

  // Create array of 4 slots (for 2x2 grid) - place photos by their photoIndex
  const photoSlots = Array.from({ length: 4 }, (_, tileIndex) => {
    return photosState.find(photo => photo.photoIndex === tileIndex) || null;
  });

  const renderPhotoTile = (photo, index) => {
    // Show upload progress tile if uploading to this specific tile
    if (!photo && index === uploadingTileIndex && uploadProgress !== null) {
      return (
        <View key={`upload-progress-${index}`} style={styles.photoTile}>
          <View style={styles.uploadProgressContainer}>
            <View style={styles.uploadProgressCircle}>
              <Text style={styles.uploadProgressText}>
                {Math.round(uploadProgress)}%
              </Text>
            </View>
            <Text style={styles.uploadProgressLabel}>Uploading...</Text>
          </View>
        </View>
      );
    }
    
    if (photo) {
      // Tile with existing photo
      return (
        <View key={`photo-${index}`} style={styles.photoTile}>
          <TouchableOpacity 
            style={styles.photoTileImageContainer}
            onPress={() => {
              setActivePhotoIndex(index);
              setFullscreenPhotoVisible(true);
            }}
          >
            <Image
              source={{ uri: photo.securedUrl }}
              style={styles.photoTileImage}
              resizeMode="cover"
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.deleteTileButton}
            onPress={() => confirmDelete(photo.photoId)}
          >
            <SvgIcon name="trash" size={24} color={colors.white} />
          </TouchableOpacity>
          {index === 0 && (
            <View style={styles.profileBadge}>
              <Text style={styles.profileBadgeText}>Profile</Text>
            </View>
          )}
        </View>
      );
    } else {
      // Empty tile with upload option
      return (
        <View key={`empty-${index}`} style={styles.photoTile}>
          <TouchableOpacity 
            style={styles.emptyTile}
            onPress={() => pickImage(index)}
            disabled={isLoading || uploadProgress !== null}
          >
            <SvgIcon name="add-circle" size={48} color={colors.accent} />
            <Text style={styles.emptyTileText}>Add Photo</Text>
          </TouchableOpacity>
        </View>
      );
    }
  };

  return (
    <View style={styles.container}>
      {!editMode ? (
        // Original Design - View Mode
        <ScrollView 
          style={styles.scrollViewContainer}
          showsVerticalScrollIndicator={true}
        >
          <View style={styles.photoHeaderFullWidth}>
            <TouchableOpacity 
              style={[styles.photoContainerFullWidth, { height: photoHeight }]}
              onPress={() => setFullscreenPhotoVisible(true)}
              activeOpacity={0.9}
            >
              <Image
                source={{ uri: currentPhoto?.securedUrl || defaultIcon }}
                style={styles.mainPhotoFullWidth}
                resizeMode="cover"
              />
            </TouchableOpacity>
            {/* Edit button - only shown if user has permission */}
            {(isOwnProfile || isMediatorProfile) && (
              <TouchableOpacity 
                style={styles.editButtonTop} 
                onPress={() => setEditMode(true)}
              >
                <SvgIcon name="create" size={28} color={colors.accent} />
              </TouchableOpacity>
            )}
            {photos.length > 0 && (
              <View style={styles.thumbnailContainerFullWidth}>
                <ScrollView 
                  horizontal 
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.thumbnailScrollContent}
                >
                  {photos.map((photo, index) => (
                    <TouchableOpacity
                      key={photo.photoId}
                      onPress={() => setActivePhotoIndex(index)}
                      style={[
                        styles.thumbnail,
                        activePhotoIndex === index && styles.activeThumbnail
                      ]}
                    >
                      <Image
                        source={{ uri: photo.securedUrl }}
                        style={styles.thumbnailImage}
                      />
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            )}
            {photos.length === 0 && (
              <View style={styles.noPhotosContainer}>
                <SvgIcon name="image-outline" size={48} color={colors.textSecondary} />
                <Text style={styles.noPhotosText}>No photos available</Text>
                <Text style={styles.noPhotosSubtext}>Upload photos to showcase profile</Text>
              </View>
            )}
          </View>
        </ScrollView>
      ) : (
        // Edit Mode - 2x2 Grid Layout
        <>
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Photos ({photos.length}/4)</Text>
            <TouchableOpacity 
              style={styles.editButton} 
              onPress={handleDoneEditing}
            >
              <SvgIcon name="checkmark-circle" size={28} color={colors.accent} />
              <Text style={styles.editButtonText}>Done</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.photoGrid}>
            <View style={styles.photoRow}>
              {renderPhotoTile(photoSlots[0], 0)}
              {renderPhotoTile(photoSlots[1], 1)}
            </View>
            <View style={styles.photoRow}>
              {renderPhotoTile(photoSlots[2], 2)}
              {renderPhotoTile(photoSlots[3], 3)}
            </View>
          </View>
        </>
      )}

      {isLoading && (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Processing...</Text>
        </View>
      )}
      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}
      {renderFullscreenPhotoModal()}

      {/* Custom Dialog Modal */}
      <Modal visible={dialog.visible} transparent animationType="fade">
        <View style={styles.dialogOverlay}>
          <View style={styles.dialogContainer}>
            <View style={[
              styles.dialogHeader,
              dialog.type === 'success' ? styles.dialogHeaderSuccess : 
              dialog.type === 'error' ? styles.dialogHeaderError :
              styles.dialogHeaderConfirm
            ]}>
              <SvgIcon 
                name={
                  dialog.type === 'success' ? 'checkmark-circle' : 
                  dialog.type === 'error' ? 'alert-circle' :
                  'help-circle'
                } 
                size={50} 
                color="#fff" 
              />
            </View>
            <View style={styles.dialogBody}>
              <Text style={styles.dialogTitle}>{dialog.title}</Text>
              <Text style={styles.dialogMessage}>{dialog.message}</Text>
              <View style={styles.dialogButtons}>
                {dialog.onConfirm ? (
                  <>
                    <TouchableOpacity 
                      style={[styles.dialogButton, styles.dialogButtonCancel]}
                      onPress={hideDialog}
                    >
                      <Text style={styles.dialogButtonText}>Cancel</Text>
                    </TouchableOpacity>
                    <TouchableOpacity 
                      style={[styles.dialogButton, styles.dialogButtonConfirm]}
                      onPress={handleDialogConfirm}
                    >
                      <Text style={styles.dialogButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </>
                ) : (
                  <TouchableOpacity 
                    style={[
                      styles.dialogButton, 
                      styles.dialogButtonSingle,
                      dialog.type === 'success' ? styles.dialogButtonSuccess : styles.dialogButtonError
                    ]}
                    onPress={handleDialogConfirm}
                  >
                    <Text style={styles.dialogButtonText}>OK</Text>
                  </TouchableOpacity>
                )}
              </View>
            </View>
          </View>
        </View>
      </Modal>

      {/* Toast Notification */}
      {toast.visible && (
        <View style={styles.toastContainer}>
          <View style={styles.toast}>
            <SvgIcon name="cloud-offline" size={24} color="#fff" />
            <Text style={styles.toastText}>{toast.message}</Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollViewContainer: {
    flex: 1,
  },
  // Original Design Styles
  photoHeaderFullWidth: {
    backgroundColor: colors.primary,
  },
  photoContainerFullWidth: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
    // height set dynamically via inline style
  },
  mainPhotoFullWidth: {
    width: '100%',
    height: '100%',
    backgroundColor: colors.primary,
  },
  editButtonTop: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: 20,
    padding: 8,
  },
  thumbnailContainerFullWidth: {
    flexDirection: 'row',
    paddingVertical: 10,
    backgroundColor: colors.primary,
  },
  thumbnailScrollContent: {
    flexGrow: 1,
    justifyContent: 'center',
    paddingHorizontal: 10,
  },
  thumbnail: {
    width: 70,
    height: 70,
    borderRadius: 8,
    borderWidth: 3,
    borderColor: 'transparent',
    marginHorizontal: 4,
  },
  activeThumbnail: {
    borderColor: colors.accent,
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    borderRadius: 6,
  },
  noPhotosContainer: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 30,
    paddingHorizontal: 20,
    backgroundColor: colors.primary,
  },
  noPhotosText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginTop: 12,
  },
  noPhotosSubtext: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 4,
  },
  // Edit Mode Grid Styles
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.white,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.text,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  editButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.accent,
    marginLeft: 4,
  },
  photoGrid: {
    padding: 12,
    backgroundColor: colors.white,
  },
  photoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  photoTile: {
    width: '48.5%',
    aspectRatio: 3/4,
    borderRadius: 12,
    overflow: 'hidden',
    backgroundColor: colors.backgroundLight,
    position: 'relative',
  },
  photoTileImageContainer: {
    width: '100%',
    height: '100%',
  },
  photoTileImage: {
    width: '100%',
    height: '100%',
  },
  deleteTileButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: 'rgba(220, 38, 38, 0.95)',
    borderRadius: 20,
    padding: 8,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 3,
  },
  profileBadge: {
    position: 'absolute',
    bottom: 8,
    left: 8,
    backgroundColor: colors.accent,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  profileBadgeText: {
    color: colors.white,
    fontSize: 11,
    fontWeight: '600',
  },
  emptyTile: {
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.backgroundLight,
    borderWidth: 2,
    borderColor: colors.border,
    borderStyle: 'dashed',
    borderRadius: 12,
  },
  emptyTileText: {
    fontSize: 14,
    color: colors.textSecondary,
    marginTop: 8,
    fontWeight: '500',
  },
  fullscreenModal: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButton: {
    position: 'absolute',
    top: 40,
    right: 20,
    zIndex: 10,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 20,
    padding: 8,
  },
  fullscreenPhotoContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  fullscreenPhoto: {
    width: '100%',
    height: '100%',
  },
  navButton: {
    position: 'absolute',
    top: '50%',
    marginTop: -20,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderRadius: 20,
    padding: 8,
    zIndex: 10,
  },
  navButtonLeft: {
    left: 20,
  },
  navButtonRight: {
    right: 20,
  },
  photoCounter: {
    position: 'absolute',
    bottom: 40,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  photoCounterText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
  },
  zoomControls: {
    position: 'absolute',
    bottom: 80,
    flexDirection: 'row',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    borderRadius: 25,
    padding: 8,
    gap: 8,
  },
  zoomButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  zoomButtonText: {
    color: colors.white,
    fontSize: 12,
    fontWeight: '600',
  },
  uploadButtonContainer: {
    alignItems: 'center',
    marginVertical: 20,
    paddingHorizontal: 16,
  },
  uploadButton: {
    backgroundColor: colors.accent,
    borderRadius: 12,
    paddingVertical: 16,
    paddingHorizontal: 32,
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    minWidth: 200,
    elevation: 3,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  uploadButtonText: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '600',
    marginTop: 8,
  },
  uploadSubText: {
    color: colors.white,
    fontSize: 12,
    marginTop: 4,
    opacity: 0.9,
  },
  loadingContainer: {
    alignItems: 'center',
    marginVertical: 12,
    padding: 12,
    backgroundColor: colors.backgroundLight,
    borderRadius: 8,
    marginHorizontal: 16,
  },
  loadingText: {
    color: colors.accent,
    fontSize: 14,
    fontWeight: '500',
  },
  errorContainer: {
    alignItems: 'center',
    marginVertical: 12,
    padding: 12,
    backgroundColor: '#FEE2E2',
    borderRadius: 8,
    marginHorizontal: 16,
  },
  errorText: {
    color: colors.error,
    fontSize: 14,
    textAlign: 'center',
  },
  uploadProgressContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
  },
  uploadProgressCircle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.accent,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  uploadProgressText: {
    color: colors.white,
    fontSize: 20,
    fontWeight: 'bold',
  },
  uploadProgressLabel: {
    color: colors.text,
    fontSize: 14,
    fontWeight: '500',
  },
  // Dialog styles
  dialogOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialogContainer: {
    backgroundColor: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
  },
  dialogHeader: {
    padding: 30,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogHeaderSuccess: {
    backgroundColor: colors.success || '#10B981',
  },
  dialogHeaderError: {
    backgroundColor: colors.error || '#EF4444',
  },
  dialogHeaderConfirm: {
    backgroundColor: colors.accent,
  },
  dialogBody: {
    padding: 24,
    alignItems: 'center',
  },
  dialogTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 12,
    textAlign: 'center',
  },
  dialogMessage: {
    fontSize: 16,
    color: colors.textSecondary || '#6B7280',
    marginBottom: 24,
    textAlign: 'center',
    lineHeight: 22,
  },
  dialogButtons: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
    justifyContent: 'center',
  },
  dialogButton: {
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    minWidth: 100,
    alignItems: 'center',
  },
  dialogButtonSingle: {
    minWidth: 120,
    paddingHorizontal: 40,
  },
  dialogButtonSuccess: {
    backgroundColor: colors.success || '#10B981',
  },
  dialogButtonError: {
    backgroundColor: colors.error || '#EF4444',
  },
  dialogButtonConfirm: {
    backgroundColor: colors.error || '#EF4444',
  },
  dialogButtonCancel: {
    backgroundColor: '#9CA3AF',
  },
  dialogButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  // Toast notification styles
  toastContainer: {
    position: 'absolute',
    top: 20,
    left: 20,
    right: 20,
    alignItems: 'center',
    zIndex: 9999,
  },
  toast: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    gap: 12,
    elevation: 5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  toastText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
});

export default PhotosTabScreen;
